import { Typography } from "antd"
import React, { memo, useMemo } from "react"

import { SETTINGS_PERMISSIONS_COLUMNS } from "../constants/columns.ts"
import {
  type SectionLayout,
  sectionFeatures,
  sectionRows
} from "../helpers/section-layout.ts"
import { getColKey } from "../helpers/utils.ts"
import type {
  ColumnKey,
  OnBulkChange,
  OnFeaturesToggle,
  PermissionsMode,
  SectionRecord
} from "../types.ts"
import type {
  PermissionDisplayMap,
  PermissionFeatures
} from "../types/permissions.types.ts"
import { PermissionsActionRow } from "./PermissionsActionRow.tsx"
import { PermissionsColumnToggleRow } from "./PermissionsColumnToggleRow.tsx"
import { PermissionsFeaturesBlock } from "./PermissionsFeaturesBlock.tsx"
import { PermissionsInheritedRow } from "./PermissionsInheritedRow.tsx"

const { Text } = Typography

type SettingsPermissionsCardProps = {
  parentKey: keyof PermissionFeatures
  subKeys: SectionRecord
  onBulkChange: OnBulkChange
  onFeaturesToggle?: OnFeaturesToggle
  isUpdating?: boolean
  disabled?: boolean
  displayMap?: PermissionDisplayMap | null
  layout?: SectionLayout
} & PermissionsMode

export const PermissionsCard = memo(
  ({
    parentKey,
    subKeys,
    onBulkChange,
    onFeaturesToggle,
    isUpdating,
    disabled,
    displayMap,
    layout,
    ...rest
  }: SettingsPermissionsCardProps) => {
    const { features, ...restSubKeys } = subKeys

    const modeProps: PermissionsMode = rest?.asForm
      ? { asForm: true }
      : { onItemChange: rest?.onItemChange }

    const rows = useMemo(
      () => sectionRows(parentKey, restSubKeys, layout),
      [parentKey, restSubKeys, layout]
    )
    const shownFeatures = useMemo(
      () => sectionFeatures(parentKey, features, layout),
      [parentKey, features, layout]
    )
    const inherited = useMemo(() => layout?.inherited ?? [], [layout])

    const columns = useMemo(() => {
      const present = new Set(
        [...rows, ...inherited].flatMap(({ actions }) =>
          Object.keys(actions)
            .map((action) => getColKey(action, actions))
            .filter((key): key is ColumnKey => key !== null)
        )
      )
      // A section whose rights are none of create/view/edit/delete — e-signature
      // was the first — has nothing to put in the grid, so it gets no columns.
      if (present.size === 0) return []
      return present.has("full_access")
        ? SETTINGS_PERMISSIONS_COLUMNS
        : SETTINGS_PERMISSIONS_COLUMNS.filter(
            (col) => col.key !== "full_access"
          )
    }, [rows, inherited])

    const gridTemplateColumns = `1fr repeat(${columns.length}, 100px)`

    return (
      <div>
        {columns.length > 0 && (
          <>
            {/* Column headers */}
            <div
              className={
                "py-1.5 px-4 grid border-b border-border bg-background"
              }
              style={{ gridTemplateColumns }}
            >
              <div />
              {columns.map((col) => (
                <Text
                  key={col.key}
                  type="secondary"
                  className={"text-center text-xs"}
                >
                  {col.label}
                </Text>
              ))}
            </div>

            {rows.length > 0 && (
              <PermissionsColumnToggleRow
                parentKey={parentKey}
                rows={rows}
                columns={columns}
                gridTemplateColumns={gridTemplateColumns}
                onBulkChange={onBulkChange}
                isUpdating={isUpdating}
                disabled={disabled}
              />
            )}
          </>
        )}

        {rows.map((row) => (
          <PermissionsActionRow
            key={`${row.parentKey}.${row.subKey}`}
            parentKey={row.parentKey}
            subKey={row.subKey}
            label={row.label}
            actions={row.actions}
            columns={columns}
            gridTemplateColumns={gridTemplateColumns}
            isUpdating={isUpdating}
            displayMap={displayMap}
            {...modeProps}
          />
        ))}

        {inherited.map((row) => (
          <PermissionsInheritedRow
            key={`${row.parentKey}.${row.subKey}`}
            row={row}
            columns={columns}
            gridTemplateColumns={gridTemplateColumns}
            displayMap={displayMap}
          />
        ))}

        {shownFeatures.length > 0 && (
          <PermissionsFeaturesBlock
            features={shownFeatures}
            onFeaturesToggle={onFeaturesToggle}
            isUpdating={isUpdating}
            disabled={disabled}
            displayMap={displayMap}
            modeProps={modeProps}
          />
        )}
      </div>
    )
  }
)

PermissionsCard.displayName = "PermissionsCard"
